/**
 * SuggestedCandidatesTab — AI-powered matched candidates for a given job.
 * Calls the agentic service at /api/recommend/* (proxied to localhost:8000 in dev).
 * Shows in the MintlyAI section of JobViewPopup for internal jobs.
 */
import CRMTabs from '@components/Common/CRMTabs';
import QueueStatusBadge from '@components/Common/QueueStatusBadge';
import { useToast } from '@components/Common/Toast/ToastProvider';
import {
  createJobApplicationMutation,
  requestRightToRepresentMutation,
  jobApplicantsList,
} from '@constants/graphQLQueries';
import { fetchGraphQLApiData } from '@helpers/graphQLApi';
import { currentUserDetails } from '@store/accessor';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutlined';
import {
    Avatar,
    Box,
    Button,
    Chip,
    IconButton,
    LinearProgress,
    Tooltip,
    Typography,
} from '@mui/material';
import axios from 'axios';
import React, { useEffect, useRef, useState } from 'react';

interface Candidate {
  jobSeekerId: string;
  firstName: string;
  lastName: string;
  currentRole: string;
  score: number;
  recommendation: 'SHORTLIST' | 'MAYBE';
  shortReason: string;
  topStrengths: string[];
  topRisks: string[];
  missingMustHaves: string[];
  confidence: number;
}

interface SuggestedCandidatesTabProps {
  jobId: string;
}

const POLL_INTERVAL_MS = 3000;
const MAX_POLLS = 40;
const PAGE_SIZE = 10;

const getInitials = (first: string, last: string): string => {
  const f = first?.[0] ?? '';
  const l = last?.[0] ?? '';
  return (f + l).toUpperCase() || '?';
};

const getAvatarBg = (rec: string) =>
  rec === 'SHORTLIST'
    ? 'var(--acc)'
    : 'var(--amber)';

const FILTERS = ['ALL', 'SHORTLIST', 'MAYBE'] as const;

const SuggestedCandidatesTab: React.FC<SuggestedCandidatesTabProps> = ({
  jobId,
}) => {
  const [status, setStatus] = useState<
    'idle' | 'triggering' | 'running' | 'done' | 'error' | 'not_started'
  >('idle');
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [filter, setFilter] = useState<'ALL' | 'SHORTLIST' | 'MAYBE'>('ALL');
  const [suggestedPage, setSuggestedPage] = useState(0);
  const [error, setError] = useState('');
  const [appliedIds, setAppliedIds] = useState<Set<string>>(new Set());
  const [submittingId, setSubmittingId] = useState<string | null>(null);
  const { showToast } = useToast();
  const pollCount = useRef(0);
  const pollTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  /*
   * Managed hiring: put this candidate forward for the client's role, with their consent.
   *
   * This used to call createJobApplicationMutation with no status, which defaults to
   * "Submitted", and stopped there. That went around the Right to Represent gate
   * completely: the candidate was submitted to a client for a role they had never
   * applied for, without being asked and without being told, and the employer could see
   * them immediately. Every other path that sources a candidate asks first, and this was
   * the one place still going around it.
   *
   * Same two steps as doSubmitToJob in JobSeekerWorkspace, deliberately: create the row
   * as "Awaiting consent", then request consent. An application in that state is hidden
   * from the employer by a server-imposed filter in JobApplicationsService, and its
   * notifications are suppressed there too, so nothing reaches the client until the
   * candidate answers. Confirming moves it to Submitted and fires the usual alerts.
   */
  const handleSubmitCandidate = async (jobSeekerId: string) => {
    const createdBy = currentUserDetails().email;
    setSubmittingId(jobSeekerId);
    try {
      const created = await fetchGraphQLApiData(
        createJobApplicationMutation(
          jobSeekerId,
          jobId,
          createdBy,
          'Qualified',
        ),
        { showStatus: true, message: 'Requesting candidate consent' },
      );
      const applicationId = created?.data?.createJobApplication?._id;
      if (!applicationId) {
        throw new Error(
          'The application was not created, so no consent request was sent.',
        );
      }
      await fetchGraphQLApiData(
        requestRightToRepresentMutation(applicationId),
      );
      setAppliedIds((prev) => new Set(prev).add(jobSeekerId));
      showToast(
        'Consent requested. We will submit once the candidate confirms.',
        'success',
      );
    } catch (e: any) {
      console.error('Failed to submit candidate:', e);
      // Name the failure rather than guessing at it: a duplicate and a network error are
      // different problems and the operator can only act on one of them.
      const message = String(e?.message ?? '');
      showToast(
        message.includes('already been submitted')
          ? 'This candidate is already on that job.'
          : 'Failed to request consent for this candidate.',
        'error',
      );
    } finally {
      setSubmittingId(null);
    }
  };

  // Set of jobSeeker ids already on this job in ANY status, including "Awaiting consent".
  // Powers the badge and the disabled button. Not "applied": a sourced candidate never did.
  useEffect(() => {
    if (!jobId) return;
    fetchGraphQLApiData(
      jobApplicantsList(0, 500, [`{ key: "jobId", value: "${jobId}" }`]),
    )
      .then(({ data }) => {
        const ids = new Set<string>(
          (data?.getAllJobApplications?.content ?? [])
            .map((a: any) => a?.jobSeeker?._id)
            .filter(Boolean),
        );
        setAppliedIds(ids);
      })
      .catch(() => {});
  }, [jobId]);

  const clearPoll = () => {
    if (pollTimer.current) {
      clearTimeout(pollTimer.current);
      pollTimer.current = null;
    }
  };

  const fetchResults = async () => {
    try {
      const { data } = await axios.get('/api/recommend/results/candidates-for-job', {
        params: { jobId, limit: 50 },
      });
      setCandidates(data || []);
      setStatus('done');
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to load candidates');
      setStatus('error');
    }
  };

  const pollStatus = async () => {
    if (pollCount.current >= MAX_POLLS) {
      setStatus('error');
      setError('Timed out waiting for results');
      return;
    }
    pollCount.current += 1;

    try {
      const { data } = await axios.get('/api/recommend/status', {
        params: { jobId },
      });
      if (data.status === 'COMPLETED') {
        clearPoll();
        await fetchResults();
      } else if (data.status === 'FAILED') {
        clearPoll();
        setStatus('error');
        setError(data.error || 'Scoring run failed');
      } else {
        pollTimer.current = setTimeout(pollStatus, POLL_INTERVAL_MS);
      }
    } catch {
      pollTimer.current = setTimeout(pollStatus, POLL_INTERVAL_MS);
    }
  };

  const triggerAndPoll = async () => {
    setStatus('triggering');
    setError('');
    setCandidates([]);
    pollCount.current = 0;
    try {
      await axios.post('/api/recommend/batch/candidates-for-job', { jobId });
      setStatus('running');
      pollTimer.current = setTimeout(pollStatus, POLL_INTERVAL_MS);
    } catch (e: any) {
      setStatus('error');
      setError(e?.response?.data?.detail || 'Failed to trigger scoring');
    }
  };

  useEffect(() => {
    if (!jobId) return;

    const init = async () => {
      // 1. Always try to load existing results first (no LLM involved)
      try {
        const { data } = await axios.get('/api/recommend/results/candidates-for-job', {
          params: { jobId, limit: 50 },
        });
        if (Array.isArray(data) && data.length > 0) {
          setCandidates(data);
          setStatus('done');
          return;
        }
      } catch {
        // fall through
      }

      // 2. No cached results — check if a run is currently in progress
      try {
        const statusRes = await axios.get('/api/recommend/status', {
          params: { jobId },
        });
        if (statusRes.data?.status === 'RUNNING') {
          setStatus('running');
          pollTimer.current = setTimeout(pollStatus, POLL_INTERVAL_MS);
          return;
        }
      } catch {
        // ignore
      }

      // 3. Nothing computed yet — show the "Run" prompt instead of auto-triggering
      setStatus('not_started');
    };

    init();
    return () => clearPoll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  const isLoading =
    status === 'idle' || status === 'triggering' || status === 'running';
  const filtered =
    filter === 'ALL'
      ? candidates
      : candidates.filter((c) => c.recommendation === filter);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pagedCandidates = filtered.slice(suggestedPage * PAGE_SIZE, (suggestedPage + 1) * PAGE_SIZE);
  const start = filtered.length === 0 ? 0 : suggestedPage * PAGE_SIZE + 1;
  const end = Math.min((suggestedPage + 1) * PAGE_SIZE, filtered.length);

  return (
    <Box>
      {/* Header row */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <AutoAwesomeIcon sx={{ color: 'var(--acc)', fontSize: 20 }} />
        <Typography variant="subtitle2" className="gradient-text">
          AI-Suggested Candidates
        </Typography>
        <QueueStatusBadge
          entityId={jobId}
          jobTypes={['BATCH_CANDIDATES_FOR_JOB']}
          onDrained={fetchResults}
        />
        <Button
          size="small"
          variant="outlined"
          sx={{
            ml: 'auto',
            fontSize: 'var(--font-size-xs)',
            textTransform: 'none',
            borderColor: 'var(--acc-line)',
            color: 'var(--acc)',
          }}
          disabled={isLoading}
          onClick={triggerAndPoll}
        >
          Re-run scoring
        </Button>
      </Box>

      {isLoading && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1.5,
            py: 4,
          }}
        >
          <PeopleOutlineIcon sx={{ fontSize: 40, opacity: 0.4 }} />
          <Typography variant="body2" color="textSecondary">
            {status === 'triggering'
              ? 'Starting AI candidate match…'
              : 'Scoring candidates: this may take a moment…'}
          </Typography>
          <LinearProgress sx={{ width: '60%', borderRadius: 1 }} />
        </Box>
      )}

      {status === 'not_started' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, py: 4 }}>
          <PeopleOutlineIcon sx={{ fontSize: 40, opacity: 0.4 }} />
          <Typography variant="body2" color="textSecondary">
            No AI scoring has been run for this job yet.
          </Typography>
          <Button
            size="small"
            variant="contained"
            sx={{ fontSize: 'var(--font-size-xs)', textTransform: 'none', backgroundColor: 'var(--color-primary)' }}
            onClick={triggerAndPoll}
          >
            Run AI Scoring
          </Button>
        </Box>
      )}

      {status === 'error' && (
        <Typography variant="body2" color="error" sx={{ py: 2 }}>
          {error}
        </Typography>
      )}

      {status === 'done' && (
        <>
          {/* Filter tabs */}
          {/* CRMTabs. The bar this replaces set its indicator colour to --color-primary through
              slotProps, which is theme rule 3 (the brand blue is a fill, never a boundary: 2.85:1
              on graphite) — and it survived because guardrail 3 reads stylesheets, and this was
              set in JS. CRMTabs owns the indicator, so there is nothing left to get wrong. */}
          <CRMTabs
            sticky={false}
            className="crm-tab-bar--embedded"
            value={FILTERS.indexOf(filter)}
            onChange={(i) => { setFilter(FILTERS[i]); setSuggestedPage(0); }}
            tabs={[
              { label: `All (${candidates.length})` },
              { label: `Shortlist (${candidates.filter((c) => c.recommendation === 'SHORTLIST').length})` },
              { label: `Maybe (${candidates.filter((c) => c.recommendation === 'MAYBE').length})` },
            ]}
          />

          {filtered.length === 0 && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1,
                py: 4,
                color: 'var(--color-text-muted)',
              }}
            >
              <PeopleOutlineIcon sx={{ fontSize: 40, opacity: 0.4 }} />
              <Typography variant="body2" color="textSecondary">
                No candidates in this category.
              </Typography>
            </Box>
          )}

          {/* Candidate rows */}
          {pagedCandidates.map((c, idx) => (
            <Box
              key={c.jobSeekerId}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1.5,
                p: 1.5,
                mb: 1,
                border: '1px solid var(--color-border-light)',
                borderRadius: 1,
                '&:hover': { backgroundColor: 'var(--color-surface-2)' },
              }}
            >
              {/* Rank + Avatar */}
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                <Typography
                  variant="caption"
                  sx={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-3xs)', lineHeight: 1 }}
                >
                  #{suggestedPage * PAGE_SIZE + idx + 1}
                </Typography>
                <Avatar
                  sx={{
                    width: 36,
                    height: 36,
                    fontSize: 'var(--font-size-2xs)',
                    fontWeight: 700,
                    background: getAvatarBg(c.recommendation),
                  }}
                >
                  {getInitials(c.firstName, c.lastName)}
                </Avatar>
              </Box>

              {/* Info */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {c.firstName} {c.lastName}
                  </Typography>
                  <Chip
                    label={c.recommendation}
                    size="small"
                    sx={{
                      height: 18,
                      fontSize: 'var(--font-size-3xs)',
                      fontWeight: 700,
                      backgroundColor:
                        c.recommendation === 'SHORTLIST' ? 'var(--green-tint)' : 'var(--amber-tint)',
                      color:
                        c.recommendation === 'SHORTLIST' ? 'var(--green)' : 'var(--amber)',
                    }}
                  />
                  {appliedIds.has(c.jobSeekerId) && (
                    <Chip
                      icon={<CheckCircleIcon sx={{ fontSize: '12px !important' }} />}
                      label="On this job"
                      size="small"
                      sx={{
                        height: 18,
                        fontSize: 'var(--font-size-3xs)',
                        fontWeight: 700,
                        backgroundColor: 'var(--acc-tint)',
                        color: 'var(--acc)',
                        '& .MuiChip-icon': { color: 'var(--acc)' },
                      }}
                    />
                  )}
                </Box>

                {c.currentRole && (
                  <Typography variant="caption" color="textSecondary">
                    {c.currentRole}
                  </Typography>
                )}

                {/* Score bar */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, minWidth: 55 }}>
                    {Math.round(c.score)}% match
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(c.score, 100)}
                    sx={{
                      flex: 1,
                      height: 5,
                      borderRadius: 1,
                      backgroundColor: 'var(--color-border-light)',
                      '& .MuiLinearProgress-bar': {
                        backgroundColor:
                          c.recommendation === 'SHORTLIST' ? 'var(--green)' : 'var(--amber)',
                        borderRadius: 1,
                      },
                    }}
                  />
                </Box>

                {/* Short reason */}
                {c.shortReason && (
                  <Typography variant="caption" color="textSecondary">
                    {c.shortReason}
                  </Typography>
                )}

                {/* Strength chips */}
                {c.topStrengths.length > 0 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                    {c.topStrengths.slice(0, 2).map((s) => (
                      <Tooltip key={s} title={s}>
                        <Chip
                          label={s}
                          size="small"
                          sx={{
                            height: 18,
                            fontSize: 'var(--font-size-3xs)',
                            backgroundColor: 'var(--acc-tint)',
                            color: 'var(--acc)',
                          }}
                        />
                      </Tooltip>
                    ))}
                  </Box>
                )}
              </Box>

              {/* Actions: view profile + submit to this role */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-end' }}>
                <Tooltip title="View profile">
                  <Button
                    size="small"
                    variant="text"
                    endIcon={<OpenInNewIcon fontSize="small" />}
                    sx={{ fontSize: 'var(--font-size-xs)', textTransform: 'none', whiteSpace: 'nowrap' }}
                    onClick={() => window.open(`/jobseekers/${c.jobSeekerId}`, '_blank', 'noopener,noreferrer')}
                  >
                    View
                  </Button>
                </Tooltip>
                {appliedIds.has(c.jobSeekerId) ? (
                  <Button
                    size="small"
                    variant="text"
                    disabled
                    startIcon={<CheckCircleIcon fontSize="small" />}
                    sx={{ fontSize: 'var(--font-size-xs)', textTransform: 'none', whiteSpace: 'nowrap' }}
                  >
                    On this job
                  </Button>
                ) : (
                  <Button
                    size="small"
                    variant="contained"
                    disabled={submittingId === c.jobSeekerId}
                    sx={{ fontSize: 'var(--font-size-xs)', textTransform: 'none', whiteSpace: 'nowrap' }}
                    onClick={() => handleSubmitCandidate(c.jobSeekerId)}
                  >
                    {submittingId === c.jobSeekerId
                      ? 'Requesting...'
                      : 'Request consent'}
                  </Button>
                )}
              </Box>
            </Box>
          ))}

          {/* Pagination */}
          {filtered.length > PAGE_SIZE && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mt: 1.5 }}>
              <Tooltip title="Previous page">
                <span>
                  <IconButton size="small" disabled={suggestedPage === 0} onClick={() => setSuggestedPage((p) => p - 1)} sx={{ color: 'var(--color-text-secondary)' }}>
                    <ChevronLeftIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Typography variant="caption" sx={{ color: 'var(--color-text-muted)', minWidth: 80, textAlign: 'center' }}>
                {start}-{end} of {filtered.length}
              </Typography>
              <Tooltip title="Next page">
                <span>
                  <IconButton size="small" disabled={suggestedPage >= totalPages - 1} onClick={() => setSuggestedPage((p) => p + 1)} sx={{ color: 'var(--color-text-secondary)' }}>
                    <ChevronRightIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default SuggestedCandidatesTab;
